import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useProducerWithThunks } from 'common/hooks/useProducer';

import SubNavBar from 'features/banking/customerBanking/components/subNav';
import {
  DepositCard,
  WithdrawCard,
} from 'features/banking/customerBanking/components/cards';
import { Route, Redirect, Switch } from 'react-router';
import FadeIn from 'react-fade-in';
import { WithdrawalCancelModal } from '../agentBanking/components/withdrawModalCards';
import { ThemedModal } from 'common/components/cardStyles';
import {
  DepositAcceptModal,
  DepositDeclineModal,
} from '../agentBanking/components/depositModalCards';
import { ClientsTable } from './components/clientsTable';
import { WalletsTable } from './components/walletsTable';
import {
  ClientTransfer,
  ClientTransferRequest,
} from 'common/state/banking/transfers/actions';
import { WithdrawalsTable } from './components/widthdrawalsTable';
import { DepositsTable } from './components/depositsTable';
import { StyledButton } from 'common/components/buttons';
import { PendingTransfersTable } from './components/pendingTransfers';
import { externalBankAccountsRecipe } from 'common/state/banking/recipes';
import { defaultExternalBankAccountsState } from 'common/state/banking/states';
import useSelector from 'common/hooks/useSelector';
import { doGetExternalBankAccounts } from 'common/state/banking/thunkActions';

const Container = styled.div`
  margin: 1rem;
`;

const CardContainer = styled.div`
  margin-top: 30px;
`;

export type TransactionMode = null | 'deposit' | 'withdraw';

export type ModalMode =
  | null
  | 'withdrawalCancel'
  | 'depositAccept'
  | 'depositDecline';

const CustomerBanking = () => {
  const [accountsState, dispatch] = useProducerWithThunks(
    externalBankAccountsRecipe,
    defaultExternalBankAccountsState
  );

  React.useEffect(() => {
    dispatch(doGetExternalBankAccounts());
  }, [dispatch]);

  const { accounts } = accountsState;
  const { wallets } = useSelector((state) => state.wallets);

  const [selectedWithdrawalRow, setSelectedWithdrawalRow] =
    useState<ClientTransferRequest>(null);
  const [selectedDepositRow, setSelectedDepositRow] =
    useState<ClientTransfer>(null);
  const [transactionMode, setTransactionMode] = useState<TransactionMode>(null);

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const closeModal = useCallback(() => setModalMode(null), []);

  /**
   * We need to track if a modal successfully updated the database
   * If it did, we need to refresh the table
   * If the modal was a success, reset it to false for the next modal and trigger
   * A re-render for the Table routes
   */
  const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);
  useEffect(() => {
    setShouldRefresh(false);
  }, [shouldRefresh]);

  return (
    <div>
      <SubNavBar />
      <Container>
        <div>
          <StyledButton
            onClick={() =>
              setTransactionMode(
                transactionMode === 'deposit' ? null : 'deposit'
              )
            }
            disabled={!wallets?.length}
          >
            Deposit
          </StyledButton>
          <StyledButton
            onClick={() =>
              setTransactionMode(
                transactionMode === 'withdraw' ? null : 'withdraw'
              )
            }
            disabled={!wallets?.length}
          >
            Withdraw
          </StyledButton>
        </div>

        <CardContainer>
          {transactionMode === 'deposit' && (
            <DepositCard
              setTransactionMode={setTransactionMode}
              wallets={wallets}
              bankAccounts={accounts}
            />
          )}
          {transactionMode === 'withdraw' && (
            <WithdrawCard
              setTransactionMode={setTransactionMode}
              wallets={wallets}
              bankAccounts={accounts}
            />
          )}
        </CardContainer>

        <WalletsTable />
        <ClientsTable />
        <Switch>
          <Route path="/banking/deposits">
            <DepositsTable />
          </Route>
          <Route path="/banking/withdrawals">
            <WithdrawalsTable
              setSelectedRow={setSelectedWithdrawalRow}
              setModalMode={setModalMode}
            />
          </Route>
          <Route path="/banking/transfers">
            <PendingTransfersTable
              setSelectedRow={setSelectedDepositRow}
              setModalMode={setModalMode}
            />
          </Route>
          <Route render={() => <Redirect to="/banking/deposits" />} />
        </Switch>

        <ThemedModal open={modalMode !== null}>
          {modalMode === 'withdrawalCancel' && (
            <FadeIn>
              <WithdrawalCancelModal
                selectedRow={selectedWithdrawalRow}
                setShouldRefresh={setShouldRefresh}
                closeModal={closeModal}
              />
            </FadeIn>
          )}
          {modalMode === 'depositAccept' && (
            <FadeIn>
              <DepositAcceptModal
                selectedRow={selectedDepositRow}
                setShouldRefresh={setShouldRefresh}
                closeModal={closeModal}
              />
            </FadeIn>
          )}
          {modalMode === 'depositDecline' && (
            <FadeIn>
              <DepositDeclineModal
                selectedRow={selectedDepositRow}
                setShouldRefresh={setShouldRefresh}
                closeModal={closeModal}
              />
            </FadeIn>
          )}
        </ThemedModal>
      </Container>
    </div>
  );
};

export default CustomerBanking;
